export type ResearchPhoneCountryOption = {
  value: string;
  dialCode: string;
  labelEn: string;
  labelEs: string;
};

export const RESEARCH_PHONE_OTHER = 'OTHER';

export const RESEARCH_PHONE_COUNTRY_OPTIONS: ResearchPhoneCountryOption[] = [
  { value: 'US', dialCode: '+1', labelEn: 'United States (+1)', labelEs: 'Estados Unidos (+1)' },
  { value: 'CA', dialCode: '+1', labelEn: 'Canada (+1)', labelEs: 'Canadá (+1)' },
  { value: 'DO', dialCode: '+1809', labelEn: 'Dominican Republic (+1-809)', labelEs: 'República Dominicana (+1-809)' },
  { value: 'VE', dialCode: '+58', labelEn: 'Venezuela (+58)', labelEs: 'Venezuela (+58)' },
  { value: 'MX', dialCode: '+52', labelEn: 'Mexico (+52)', labelEs: 'México (+52)' },
  { value: 'CO', dialCode: '+57', labelEn: 'Colombia (+57)', labelEs: 'Colombia (+57)' },
  { value: 'GB', dialCode: '+44', labelEn: 'United Kingdom (+44)', labelEs: 'Reino Unido (+44)' },
  { value: 'ES', dialCode: '+34', labelEn: 'Spain (+34)', labelEs: 'España (+34)' },
  { value: 'BR', dialCode: '+55', labelEn: 'Brazil (+55)', labelEs: 'Brasil (+55)' },
  { value: 'AR', dialCode: '+54', labelEn: 'Argentina (+54)', labelEs: 'Argentina (+54)' },
  { value: 'PE', dialCode: '+51', labelEn: 'Peru (+51)', labelEs: 'Perú (+51)' },
  { value: 'CL', dialCode: '+56', labelEn: 'Chile (+56)', labelEs: 'Chile (+56)' },
  { value: 'EC', dialCode: '+593', labelEn: 'Ecuador (+593)', labelEs: 'Ecuador (+593)' },
  { value: RESEARCH_PHONE_OTHER, dialCode: '', labelEn: 'Other country code', labelEs: 'Otro código de país' },
];

export type ResearchPhoneRow = {
  country: string;
  nationalNumber: string;
  /** Preserved E.164 when parsing could not split country. */
  rawE164?: string;
};

const dialDigits = (dialCode: string): string => String(dialCode || '').replace(/\D/g, '');

export function normalizeResearchPhoneE164(dialCode: string, nationalNumber: string): string {
  const national = String(nationalNumber || '').replace(/\D/g, '');
  const cc = dialDigits(dialCode);
  if (!national && !cc) return '';
  if (!cc) {
    const raw = String(nationalNumber || '').trim();
    if (raw.startsWith('+')) return `+${raw.replace(/\D/g, '')}`;
    return national ? `+${national}` : '';
  }
  if (!national) return '';
  return `+${cc}${national}`;
}

function matchDialPrefix(digits: string): { country: string; dialCode: string; national: string } | null {
  const sorted = [...RESEARCH_PHONE_COUNTRY_OPTIONS]
    .filter((row) => row.value !== RESEARCH_PHONE_OTHER && row.dialCode)
    .sort((a, b) => dialDigits(b.dialCode).length - dialDigits(a.dialCode).length);
  for (const row of sorted) {
    const cc = dialDigits(row.dialCode);
    if (cc && digits.startsWith(cc)) {
      return { country: row.value, dialCode: row.dialCode, national: digits.slice(cc.length) };
    }
  }
  return null;
}

export function parseResearchPhonesCsv(value: string): ResearchPhoneRow[] {
  const tokens = String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  if (!tokens.length) {
    return [{ country: 'US', nationalNumber: '' }];
  }
  return tokens.map((token) => {
    if (token.startsWith('+')) {
      const digits = token.replace(/\D/g, '');
      const matched = matchDialPrefix(digits);
      if (matched) {
        return {
          country: matched.country,
          nationalNumber: matched.national,
          rawE164: token,
        };
      }
      return {
        country: RESEARCH_PHONE_OTHER,
        nationalNumber: token,
        rawE164: token,
      };
    }
    return {
      country: 'US',
      nationalNumber: token.replace(/\D/g, ''),
    };
  });
}

export function formatResearchPhonesCsv(rows: ResearchPhoneRow[]): string {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    let e164 = '';
    if (row.country === RESEARCH_PHONE_OTHER) {
      const raw = String(row.rawE164 || row.nationalNumber || '').trim();
      e164 = raw.startsWith('+') ? `+${raw.replace(/\D/g, '')}` : raw;
    } else {
      const option = RESEARCH_PHONE_COUNTRY_OPTIONS.find((item) => item.value === row.country);
      e164 = normalizeResearchPhoneE164(option?.dialCode || '+1', row.nationalNumber);
    }
    if (!e164 || seen.has(e164)) continue;
    seen.add(e164);
    out.push(e164);
  }
  return out.join(', ');
}
